<script setup lang="ts">
import {computed, ref} from 'vue'
import {useLocalization} from '@hantera/portal-app'
import {ElasticInput, TextInput, useClose} from "@hantera/design-system";
import {useIamEventsStore} from '@/stores/iam/iam-events-store'

const props = defineProps<{
  onCreated?: (client: any) => void
  onCancel?: () => void
  iamService: any
}>()

const t = useLocalization('iam.clients')
const iamEvents = useIamEventsStore()

const name = ref('')
const description = ref('')
const isCreating = ref(false)
const errorMessage = ref('')
const close = useClose()

const isValid = computed(() => {
  return name.value.trim().length > 0 && name.value.length <= 200
})

async function create() {
  if (!isValid.value) return

  errorMessage.value = ''
  isCreating.value = true

  try {
    const client = await props.iamService.createClient({
      name: name.value.trim(),
      description: description.value.trim() || undefined,
    })

    // Broadcast to all ClientsView instances
    iamEvents.emitClientsListChanged()

    props.onCreated?.(client)
    close()
  } catch (error: any) {
    if (error.response?.status === 409) {
      errorMessage.value = t('create.errors.duplicateName')
    } else if (error.response?.status === 400) {
      errorMessage.value = error.response.data.message || t('create.errors.validationFailed')
    } else {
      errorMessage.value = t('create.errors.failed')
      console.error('Failed to create client:', error)
    }
  } finally {
    isCreating.value = false
  }
}

function cancel() {
  props.onCancel?.()
}
</script>

<template>
  <div class="create-client p2">
    <div class="form-field">
      <label>{{ t('create.form.name') }} *</label>
      <TextInput
        v-model="name"
        :placeholder="t('create.form.namePlaceholder')"
        maxlength="200"
        @keydown.enter="isValid && !isCreating && create()"
      />
      <p class="field-help">{{ t('create.form.nameHelp') }}</p>
    </div>

    <div class="form-field">
      <label>{{ t('create.form.description') }}</label>
      <ElasticInput
        v-model="description"
        :placeholder="t('create.form.descriptionPlaceholder')"
        :maxlength="500"
      />
    </div>

    <div v-if="errorMessage" class="error-message">
      {{ errorMessage }}
    </div>

    <div class="dialog-actions">
      <button @click="cancel" class="btn-secondary" :disabled="isCreating">
        {{ t('actions.cancel') }}
      </button>
      <button @click="create" class="btn-primary" :disabled="!isValid || isCreating">
        {{ isCreating ? t('actions.creating') : t('actions.create') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.create-client {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 400px;
}

textarea.text-input {
  resize: vertical;
  font-family: inherit;
}

.field-help {
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
  margin: 0;
}

.error-message {
  color: var(--color-error);
  background: var(--color-error-bg);
  padding: 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid var(--color-border);
}
</style>
